import React, { useState, useEffect } from "react";
import { ProductCard } from "./ProductCard";
import { Product } from "../types";
import { fetchProductsData } from "../api/catalogApi";
import clsx from "clsx";
import {
  parseQueryParams,
  buildQueryString,
  updateQueryParams,
} from "../utils/queryParams";
import { eventBus, EVENTS } from "../utils/eventBus";
// import { viewSwitcher } from "./ViewSwitcher";

interface ProductsProps {
  groupId: string;
  cartId: string;
  productFlags: Record<string, string[]>;
  productBadges: Record<string, string[]>;
  couponDiscountAmounts: Record<string, number>;
}

export const Products: React.FC<ProductsProps> = ({
  groupId,
  cartId,
  productFlags,
  productBadges,
  couponDiscountAmounts,
}) => {
  const [productData, setProductData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(24);
  const [sortOption, setSortOption] = useState("none");

  // viewSwitcher();

  // Get current page and other parameters from URL on component mount
  useEffect(() => {
    const params = parseQueryParams();

    // Set page from URL
    const pageParam = params.page as string;
    if (pageParam) {
      setCurrentPage(parseInt(pageParam, 10));
    }

    // Set items per page from URL
    const itemsPerPageParam = params.items_per_page as string;
    if (itemsPerPageParam) {
      setItemsPerPage(parseInt(itemsPerPageParam, 10));
    }

    // Set sort option from URL
    const sortParam = params.sort as string;
    if (sortParam) {
      setSortOption(sortParam);
    }
  }, []);

  // Load products data
  const loadProducts = async (params?: any) => {
    try {
      setLoading(true);
      // Clear existing product data immediately when loading starts
      setProductData(null);

      const queryParams = params || parseQueryParams();
      const queryString = buildQueryString(queryParams);
      const data = await fetchProductsData(groupId, queryString);
      setProductData(data);
      // Emit event to notify App component about product data update
      eventBus.emit("product_data_updated", data);
    } catch (error) {
      console.error("Error loading products:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load products on mount
  useEffect(() => {
    loadProducts();
  }, [groupId]);

  // Listen for filter changes
  useEffect(() => {
    const handleFiltersChanged = (newParams: any) => {
      loadProducts(newParams);
    };

    const handlePageChanged = (page: number) => {
      setCurrentPage(page);
      const params = parseQueryParams();
      params.page = page.toString();
      updateQueryParams(params);
      loadProducts(params);
    };

    const handleSortChanged = (sort: string) => {
      setSortOption(sort);
      setCurrentPage(1);
      const params = parseQueryParams();
      params.sort = sort === "none" ? "" : "field_cash_price:" + sort;
      params.page = "1";
      updateQueryParams(params);
      loadProducts(params);
    };

    const handleItemsPerPageChanged = (itemsPerPage: number) => {
      setItemsPerPage(itemsPerPage);
      setCurrentPage(1);
      const params = parseQueryParams();
      params.items_per_page = itemsPerPage.toString();
      params.page = "1";
      updateQueryParams(params);
      loadProducts(params);
    };

    eventBus.on(EVENTS.FILTERS_CHANGED, handleFiltersChanged);
    eventBus.on(EVENTS.PAGE_CHANGED, handlePageChanged);
    eventBus.on(EVENTS.SORT_CHANGED, handleSortChanged);
    eventBus.on(EVENTS.ITEMS_PER_PAGE_CHANGED, handleItemsPerPageChanged);

    return () => {
      eventBus.off(EVENTS.FILTERS_CHANGED, handleFiltersChanged);
      eventBus.off(EVENTS.PAGE_CHANGED, handlePageChanged);
      eventBus.off(EVENTS.SORT_CHANGED, handleSortChanged);
      eventBus.off(EVENTS.ITEMS_PER_PAGE_CHANGED, handleItemsPerPageChanged);
    };
  }, [groupId]);

  // Calculate pagination values
  const totalProducts = productData?.count || 0;
  const totalPages = Math.ceil(totalProducts / itemsPerPage);

  // Calculate visible page numbers
  const getVisiblePages = () => {
    const pages = [];
    const maxVisiblePages = 3;

    if (totalPages <= maxVisiblePages) {
      // If total pages are less than max visible, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate start and end of visible pages
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if we're near the start
      if (currentPage <= maxVisiblePages) {
        end = maxVisiblePages;
      }

      // Adjust if we're near the end
      if (currentPage >= totalPages - maxVisiblePages) {
        start = totalPages - maxVisiblePages;
      }

      // Add ellipsis after first page if needed
      if (start > 2) {
        pages.push("...");
      }

      // Add middle pages (skip page 1 since it's already added)
      for (let i = start; i <= end; i++) {
        if (i !== 1) {
          pages.push(i);
        }
      }

      // Add ellipsis before last page if needed
      if (end < totalPages - 1) {
        pages.push("...");
      }

      // Always show last page (unless it's page 1 which is already shown)
      if (totalPages !== 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // First, create a function to combine flags and badges
  const getCombinedFlags = (productId: string) => {
    let flags = productFlags[productId] || [];
    let badges = productBadges[productId] || [];
    if (typeof badges === "object") {
      badges = Object.values(badges);
    }
    if (typeof flags === "object") {
      flags = Object.values(flags);
    }
    // Use Set to remove duplicates if any
    return [...new Set([...flags, ...badges])];
  };

  const handlePageChange = (page: number) => {
    const viewHeader = document.querySelector(
      "#catalog-page-root",
    ) as HTMLElement | null;

    viewHeader?.scrollIntoView({ behavior: "smooth" });
    eventBus.emit(EVENTS.PAGE_CHANGED, page);
  };

  const handleItemsPerPageChange = (value: number) => {
    eventBus.emit(EVENTS.ITEMS_PER_PAGE_CHANGED, value);
  };

  const handleSortChange = (value: string) => {
    eventBus.emit(EVENTS.SORT_CHANGED, value);
  };

  // Handle layout switcher for grid of list view
  const [layout, setLayout] = useState<"grid" | "list">("grid");

  return (
    <div className="relative">
      {/* Top Pagination and Sorting */}
      <div className="view-heading">
        <div className="view-heading__pager">
          {/* Pagination */}
          {totalPages > 1 && (
            <nav aria-label="Page navigation" className="pagination-container">
              <ul className="pagination">
                <li className="page-item">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handlePageChange(currentPage - 1);
                    }}
                    disabled={currentPage === 1}
                    className="page-link"
                    aria-label="Previous page"
                  >
                    <span className="previous-page" aria-hidden="true"></span>
                  </button>
                </li>

                {getVisiblePages().map((page, index) =>
                  page === "..." ? (
                    <li key={`ellipsis-${index}`} className="page-item">
                      <span className="page-link">...</span>
                    </li>
                  ) : (
                    <li key={page} className="page-item">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handlePageChange(page as number);
                        }}
                        className={`${
                          currentPage === page
                            ? "text-green-600 font-bold"
                            : "page-link "
                        }`}
                      >
                        {page}
                      </button>
                    </li>
                  ),
                )}

                <li className="page-item">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handlePageChange(currentPage + 1);
                    }}
                    disabled={currentPage === totalPages}
                    className="page-link"
                    aria-label="Next page"
                  >
                    <span className="next-page" aria-hidden="true"></span>
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>
        <div className="view-heading__custom-sort-pager">
          <div className="view-heading__pager">
            <label
              htmlFor="items-per-page"
              className="px-1 text-sm font-medium text-gray-700 whitespace-nowrap items-per-page-label"
              aria-label="Proizvod / Stranica"
            >
              Proizvod / Stranica:
            </label>
            <select
              id="items-per-page"
              value={itemsPerPage}
              onChange={(e) =>
                handleItemsPerPageChange(parseInt(e.target.value, 10))
              }
              className="border rounded px-3 py-1 items-per-page-select"
              aria-label="Proizvod / Stranica"
            >
              <option value={12}>12</option>
              <option value={24}>24</option>
              <option value={36}>36</option>
              <option value={64}>64</option>
            </select>
          </div>
          <div className="view-heading__sorting flex items-center gap-2">
            <label
              htmlFor="sort-option"
              className="text-sm font-medium text-gray-700 whitespace-nowrap sort-option-label"
              aria-label="Sortiraj po cijeni"
            >
              Sortiraj po cijeni
            </label>
            <select
              id="sort-option"
              value={sortOption}
              onChange={(e) => handleSortChange(e.target.value)}
              className="border rounded px-2 py-1 sort-option-select"
              aria-label="Sortiraj po cijeni"
            >
              <option value="none">Zadano</option>
              <option value="asc">{`Manja < Veća`}</option>
              <option value="desc">{`Veća > Manja`}</option>
            </select>
            <div
              className={clsx(
                "page-listing",
                layout === "grid" && "category-showing-grid",
                layout === "list" && "category-showing-list",
              )}
            >
              <div className="view__toggle-view view__toggle-view--new">
                <button
                  className={clsx(
                    "view__toggle-view--list",
                    layout === "list" && "active",
                  )}
                  onClick={() => setLayout("list")}
                >
                  <span className="visually-hidden">Show listings</span>
                </button>
                <button
                  className={clsx(
                    "view__toggle-view--grid",
                    layout === "grid" && "active",
                  )}
                  onClick={() => setLayout("grid")}
                >
                  <span className="visually-hidden">Show grid</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-view-category-page__total-results">
        Ukupno odabrano proizvoda: <span>{totalProducts}</span>
      </div>

      {/* Products Grid Container with Loading Overlay */}
      <div
        className={`relative min-h-[400px] ${
          layout !== "grid" ? "list-view-mode" : ""
        }`}
      >
        {/* Loading Overlay - Transparent with spinner */}
        {loading && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-white bg-opacity-95 backdrop-blur-sm rounded-lg shadow-lg">
            <div className="text-center p-8">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto"></div>
              <p className="mt-6 text-lg text-gray-700 font-semibold">
                Učitavanje proizvoda...
              </p>
              <p className="mt-2 text-sm text-gray-500">
                Molimo pričekajte dok se proizvodi učitavaju.
              </p>
            </div>
          </div>
        )}

        {/* Products Grid */}
        {!loading && (
          <div
            className={clsx(
              "product-grid-container grid gap-6",
              // Base mobile layout
              "grid-cols-1 sm:grid-cols-2",
              // Swap only the md+ behavior when toggling
              layout === "grid" ? "md:grid-cols-3" : "md:grid-cols-1",
            )}
          >
            {productData?.products &&
              productData?.products.map((product: Product) => (
                <ProductCard
                  key={product.nid}
                  product={product}
                  flags={getCombinedFlags(product.nid)}
                  isInCompareList={
                    productData?.compareList?.includes(Number(product.nid)) ||
                    false
                  }
                  compareFull={productData?.compareFull || false}
                  wishlist={Boolean(productData.wishlist?.[product.nid])}
                  couponDiscountAmount={
                    couponDiscountAmounts[product.nid] || 0
                  }
                  cartId={cartId}
                />
              ))}
            {(!productData?.products || productData?.products.length === 0) && (
              <div className="col-span-full text-center py-12 bg-white rounded-lg shadow">
                <p className="text-gray-500 text-lg">
                  Nema proizvoda koji odgovaraju postavljenim kriterijima.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Pagination */}
      {totalPages > 1 && (
        <div className="pagination-container mt-8">
          <nav aria-label="Page navigation">
            <ul className="pagination">
              <li className="page-item">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePageChange(currentPage - 1);
                  }}
                  disabled={currentPage === 1}
                  className="page-link"
                  aria-label="Previous page"
                >
                  <span className="previous-page" aria-hidden="true"></span>
                </button>
              </li>

              {getVisiblePages().map((page, index) =>
                page === "..." ? (
                  <li key={`ellipsis-${index}`} className="page-item">
                    <span className="page-link">...</span>
                  </li>
                ) : (
                  <li key={page} className="page-item">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handlePageChange(page as number);
                      }}
                      className={`${
                        currentPage === page
                          ? "text-green-600 font-bold"
                          : "page-link "
                      }`}
                    >
                      {page}
                    </button>
                  </li>
                ),
              )}

              <li className="page-item">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePageChange(currentPage + 1);
                  }}
                  disabled={currentPage === totalPages}
                  className="page-link"
                  aria-label="Next page"
                >
                  <span className="next-page" aria-hidden="true"></span>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};
